import {
  useCreateSnapshotApiV1DatasetDatasetIdSnapshotPost,
  useUpdateSnapshotApiV1DatasetDatasetIdSnapshotSnapshotIdPatch,
} from "../../../../vl-api/vlComponents";
import { App } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateActiveQueryByOperationId } from "../../../../vl-api/vlUtils";
import { SnapshotModalProps } from "./SnapshotModal";

type SnapshotFormValues = {
  name: string;
  description: string;
};

export const useSnapshotModal = ({
  datasetId,
  snapshot,
  close,
}: Pick<SnapshotModalProps, "close" | "datasetId" | "snapshot">) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const refetchSnapshots = () =>
    invalidateActiveQueryByOperationId(
      queryClient,
      "listSnapshotsApiV1DatasetDatasetIdSnapshotGet",
    );
  const createSnapshotMutation =
    useCreateSnapshotApiV1DatasetDatasetIdSnapshotPost({
      onSuccess: () => {
        close();
        refetchSnapshots();
        notification.success({ message: "Snapshot created successfully." });
      },
      onError: () => {
        notification.error({ message: "Failed to create snapshot." });
      },
    });

  const updateSnapshotMutation =
    useUpdateSnapshotApiV1DatasetDatasetIdSnapshotSnapshotIdPatch({
      onSuccess: () => {
        close();
        refetchSnapshots();
        notification.success({ message: "Snapshot updated successfully." });
      },
      onError: () => {
        notification.error({ message: "Failed to updated snapshot." });
      },
    });

  const createSnapshot = async (values: SnapshotFormValues) =>
    createSnapshotMutation.mutate({ pathParams: { datasetId }, body: values });

  const updateSnapshot = async (values: SnapshotFormValues) =>
    updateSnapshotMutation.mutate({
      pathParams: { datasetId, snapshotId: snapshot!.snapshot_id },
      body: values,
    });

  const submit = (values: SnapshotFormValues) => {
    if (snapshot) {
      return updateSnapshot(values);
    }
    return createSnapshot(values);
  };

  return {
    isLoading: createSnapshotMutation.isPending,
    submit,
  };
};
